import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {Attributes, AttributesRow, Card} from 'platform/components';

import {useCallback} from 'react';
import {useSelector} from 'react-redux';

import {find, isNil, isNotNil, propEq} from 'ramda';

import {useGetSalePresetSettingsListQuery, useGetWarrantyProgramQuery} from '@dms/api/sales';
import {FullState, ModelSpecification, VehicleDataResponseBody} from '@dms/api/shared';
import {useGetRentalVehicleQuery, useGetSaleVehicleQuery} from '@dms/api/vehicle';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds, vehiclesRoutes} from '@dms/routes';
import {useCodeList, useIsQualitySealVisible} from '@dms/shared';
import {selectCountries, selectCondition, VehicleType} from '@dms/teas';

import {buildArray, composePath, useNavigate} from 'shared/web';

type ModelSpecificationCardProps = {
  id: string;
  type?: VehicleType;
  state: Partial<FullState>;
  modelSpecification: Partial<ModelSpecification>;
  vehicle: Partial<VehicleDataResponseBody>;
  canEditVehicle: boolean;
  canReadAdvertisingSettings: boolean;
  vehicleSourceCodeId?: string | null;
};

const ModelSpecificationCard = ({
  id,
  type,
  state,
  modelSpecification,
  vehicle,
  vehicleSourceCodeId,
  canEditVehicle,
  canReadAdvertisingSettings,
}: ModelSpecificationCardProps) => {
  const navigate = useNavigate();
  const {mileage, ownerCount, primaryKeyCount, hasCoc} = state;
  const {originCountry, firstRegistrationCountry, lastRegistrationCountry, isRegistered} =
    modelSpecification;

  const condition = useSelector(selectCondition(state.condition, type));
  const countryList = useSelector(selectCountries);

  const {data: saleVehicle} = useGetSaleVehicleQuery({vehicleId: id});
  const {data: rentalVehicle} = useGetRentalVehicleQuery({vehicleId: id});

  const {data: salePresetList} = useGetSalePresetSettingsListQuery();

  const selectedSalePreset = salePresetList?.find(
    (salePreset) => salePreset.id === saleVehicle?.salePresetId
  );

  const isSalesWarrantyEnabled = isFeatureEnabled(featureFlags.SALES_WARRANTY);
  const isQualitySealVisible = useIsQualitySealVisible();

  const warrantyProgramId = saleVehicle?.warrantyProgramId;

  // Resolve the name via the detail endpoint (not the active list): it still returns
  // a program the user selected once even after it was removed (deactivated) from the
  // active list, so the previously-chosen name is preserved.
  const {data: selectedWarrantyProgram} = useGetWarrantyProgramQuery(
    isSalesWarrantyEnabled && isNotNil(warrantyProgramId) ? {warrantyProgramId} : skipToken
  );

  const [vehicleSourceCodes] = useCodeList('vehicle_source');

  const handleOnClick = useCallback(() => {
    if (id) {
      navigate(composePath(vehiclesRoutes.editV2Tab, {params: {id, tab: 'vehicle-data'}}));
    }
  }, [id, navigate]);

  const isTrailerOrSemiTrailer =
    type === 'VEHICLETYPE_TRAILER' || type === 'VEHICLETYPE_SEMI_TRAILER';

  const rows = buildArray<AttributesRow>([])
    .add({
      label: i18n.t('entity.vehicle.labels.condition'),
      value: condition?.label,
      testId: 'condition',
    })
    .add({
      label: i18n.t('entity.vehicle.labels.countryOfOrigin'),
      value: find(propEq(originCountry, 'code'), countryList)?.name,
      testId: 'countryOfOrigin',
    })
    .add({
      label: i18n.t('entity.vehicle.labels.firstRegistrationCountry'),
      value: find(propEq(firstRegistrationCountry, 'code'), countryList)?.name,
      testId: 'firstRegistrationCountry',
    })
    .add({
      label: i18n.t('entity.vehicle.labels.lastRegistrationCountry'),
      value: find(propEq(lastRegistrationCountry, 'code'), countryList)?.name,
      testId: 'lastRegistrationCountry',
    })
    .add({
      label: i18n.t('entity.vehicle.labels.registered'),
      value: isRegistered ? i18n.t('general.labels.yes') : i18n.t('general.labels.no'),
      testId: 'registered',
    })
    .add({
      label: i18n.t('entity.vehicle.labels.hasCoc'),
      value: hasCoc ? i18n.t('general.labels.yes') : i18n.t('general.labels.no'),
      testId: 'hasCoc',
    })
    .whenNot(isTrailerOrSemiTrailer, {
      label: `${i18n.t('entity.vehicle.labels.mileage')} (${i18n.t('general.metric.km')})`,
      value: isNil(mileage) ? '' : `${mileage}`,
      testId: 'mileage',
    })
    .whenNot(isTrailerOrSemiTrailer, {
      label: `${i18n.t('entity.vehicle.labels.realMileage')} (${i18n.t('general.metric.km')})`,
      value: isNil(vehicle?.realMileage) ? '' : `${vehicle.realMileage}`,
      testId: 'realMileage',
    })
    .add({
      label: i18n.t('entity.vehicle.labels.previousOwners'),
      value: isNil(ownerCount) ? '' : `${ownerCount}`,
      testId: 'previousOwners',
    })
    .add({
      label: i18n.t('entity.vehicle.labels.numberOfKeysFull'),
      value: isNil(primaryKeyCount) ? '' : `${primaryKeyCount}`,
      testId: 'numberOfKeysFull',
    })
    .add({
      label: i18n.t('entity.vehicle.labels.vehicleRegistration'),
      value: vehicle.vehicleRegistrationNumber,
      testId: 'vehicleRegistration',
    })
    .add({
      label: i18n.t('entity.vehicle.labels.registrationCertificate'),
      value: vehicle.registrationCertificateNumber,
      testId: 'registrationCertificate',
    })
    .add({
      label: i18n.t('entity.vehicle.labels.vehicleSource'),
      value: vehicleSourceCodes?.find((source) => source.codeId === vehicleSourceCodeId)?.name,
      testId: 'vehicleSource',
    })
    .add({
      label: i18n.t('entity.vehicle.labels.servicedInAuthorizedNetwork'),
      value: isNil(vehicle.servicedInAuthorizedNetwork) // eslint-disable-line no-nested-ternary -- Pre-existing violation
        ? ''
        : vehicle.servicedInAuthorizedNetwork
          ? i18n.t('general.labels.yes')
          : i18n.t('general.labels.no'),
      testId: 'servicedInAuthorizedNetwork',
    })
    .when(
      isFeatureEnabled(featureFlags.SALES_SALE_PROGRAMS) &&
        salePresetList &&
        salePresetList.length > 0 &&
        canReadAdvertisingSettings,
      {
        label: i18n.t('entity.vehicle.labels.salePreset'),
        value: selectedSalePreset?.name,
        testId: 'salePresetId',
      }
    )
    .when(isQualitySealVisible && canReadAdvertisingSettings, {
      label: i18n.t('entity.qualitySeal.labels.qualitySeal'),
      value: saleVehicle?.qualitySeal?.displayName,
      testId: 'qualitySealKey',
    })
    .when(isNil(rentalVehicle) && isSalesWarrantyEnabled, {
      label: i18n.t('entity.warranty.labels.warrantyProgram'),
      value: selectedWarrantyProgram?.name,
      testId: 'warrantyProgramId',
    })
    .when(isNil(rentalVehicle) && isSalesWarrantyEnabled, {
      label: i18n.t('entity.vehicle.labels.warrantyDuration'),
      value: isNil(saleVehicle?.warrantyDuration)
        ? undefined
        : `${saleVehicle?.warrantyDuration} ${i18n.t('general.labels.months')}`,
      testId: 'warrantyDuration',
    })
    .add({
      label: i18n.t('entity.vehicle.labels.otherRecords'),
      value: vehicle.otherRecords,
      testId: 'otherRecords',
    });

  return (
    <Card
      title={i18n.t('entity.vehicle.labels.generalData')}
      actions={
        canEditVehicle
          ? [
              {
                type: 'button',
                variant: 'link',
                leftIcon: 'image/edit',
                onClick: handleOnClick,
                'data-testid': testIds.vehicles.vehicleData('conditionsCard-edit'),
                title: i18n.t('general.actions.edit'),
              },
            ]
          : undefined
      }
      data-testid={testIds.vehicles.vehicleData('conditionsCard')}
    >
      <Attributes
        data-testid={testIds.vehicles.vehicleData('conditionsCard-parameters')}
        rows={rows}
      />
    </Card>
  );
};

export const ConditionsCard = ModelSpecificationCard;
